import styled from '@emotion/styled'
import { Gray700, Info100, Info800 } from '@mehilainen/mds-customer/colors'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AppointmentSearchResult, CallbackSearchResult } from '../../../__generated__/api'
import { useNodeSearchParams, useSelectedDate } from '../../../xstate/selectors'
import api from '../../services/api'
import { isDentalMaintenanceError } from '../../utils/error/utils'
import { scale } from '../../utils/scale'
import { formatDate2 } from '../../utils/text'
import { DentalMaintenance } from '../DentalMaintenance/DentalMaintenance'
import { CenteredColumnFlex, ColumnFlex } from '../Layout/Layout'
import LoadingButton from '../LoadingButton/LoadingButton'
import Tiles, { LoadingTiles } from '../Tile/Tiles'
import { Text } from '../Typography/Typography'

import { mapToPractitionerGroups } from './utils'

const NoAppointments = styled.div`
  color: ${Info800};
  background: ${Info100};
  border-radius: 4px;
  padding: ${scale(1)} ${scale(1.5)};
`

const LoadMoreContainer = styled(CenteredColumnFlex)`
  margin-top: ${scale(4)};
`

const Container = styled(ColumnFlex)`
  gap: 24px;
`

interface Props {
  appointments: Array<AppointmentSearchResult | CallbackSearchResult>
  appointmentsPending: boolean
  isUserSelectedDate: boolean
  onPractitionerSelect(practitionerId: number, selectedDate: Dayjs, appointmentId?: number): void
  onSpecialistClick(appointment: AppointmentSearchResult): void
  remoteSupported: boolean
}

const maxNrOfMonthsForward = 6
const initialMonthsForward = 1
const maxNrOfSearches = 2

const PractitionerList: React.FC<React.PropsWithChildren<Props>> = ({
  appointments,
  appointmentsPending,
  isUserSelectedDate,
  onPractitionerSelect,
  onSpecialistClick,
  remoteSupported,
}) => {
  const { t, i18n } = useTranslation()
  const selectedDate = useSelectedDate()
  const searchParams = useNodeSearchParams()
  const queryClient = useQueryClient()

  const {
    data: appointmentsResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: appointmentsError,
    isLoading: initialAppointmentsLoading,
  } = useInfiniteQuery({
    queryKey: [api.v1.availablePractitionersSearch.name, searchParams, selectedDate.toISOString()],
    queryFn: ({ pageParam }) => {
      const startDate = pageParam || selectedDate.toISOString()
      const monthsForward =
        startDate !== selectedDate.toISOString()
          ? maxNrOfMonthsForward - initialMonthsForward
          : initialMonthsForward

      const response = api.v1.availablePractitionersSearch({
        ...searchParams,
        startDate,
        nroOfMonths: monthsForward,
      })
      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      const latestAppointment = [...lastPage.data].sort(
        (a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf()
      )[0]

      if (!latestAppointment || allPages.length >= maxNrOfSearches) {
        return undefined
      }

      return dayjs(latestAppointment.time).add(1, 'day').startOf('day').toISOString()
    },
    refetchOnWindowFocus: false,
    initialPageParam: selectedDate.toISOString(),
    refetchOnReconnect: false,
    refetchOnMount: false,
  })

  const isLoading = initialAppointmentsLoading || isFetchingNextPage

  const allAppointments = useMemo(() => {
    if (!appointmentsResponse) {
      return appointments
    }

    const additionalAppointments = appointmentsResponse.pages.map((p) => p.data).flat()
    return [...appointments, ...additionalAppointments].filter(
      (a: AppointmentSearchResult) => !a.isResourceList
    )
  }, [appointments, appointmentsResponse])

  useEffect(() => {
    queryClient.resetQueries({
      queryKey: ['availablePractitioners', searchParams, selectedDate.toISOString()],
    })
  }, [searchParams, queryClient, selectedDate])

  const searchStartDate = appointments?.length
    ? dayjs(appointments[appointments.length - 1].time)
        .add(1, 'day')
        .startOf('day')
    : selectedDate

  const { first, later } = mapToPractitionerGroups(
    allAppointments,
    selectedDate.isToday() ? searchStartDate : selectedDate
  )

  const indexRef = useRef<number>(-1)

  const displayDentalMaintenance = isDentalMaintenanceError(appointmentsError)

  if (displayDentalMaintenance) {
    return <DentalMaintenance variant="nested" />
  }

  if (appointmentsPending) {
    return <LoadingTiles variant="specialist" />
  }

  return (
    <Container>
      {first.length > 0 ? (
        <>
          <Text as="h2" $weight="Regular" $size={300} $color={Gray700} $uppercase>
            {selectedDate.isToday()
              ? t('component.practitionerList.availableFirst')
              : t('component.practitionerList.availableSelectedDate', {
                  date: formatDate2(selectedDate, t),
                  lng: i18n.language,
                })}
          </Text>
          <Tiles
            appointments={first}
            variant="specialist"
            onAppointmentClick={(appointment) => {
              onPractitionerSelect(
                appointment.specialistId,
                selectedDate,
                appointment.appointmentId
              )
            }}
            onSpecialistClick={(appointment) => {
              onSpecialistClick(appointment)
            }}
            remoteSupported={remoteSupported}
            notifications={[]}
            onDcNotificationClick={() => {}}
            focusIndex={indexRef.current}
          />
        </>
      ) : isUserSelectedDate ? (
        <NoAppointments>{t('component.appointmentList.noAppointments')}</NoAppointments>
      ) : null}
      {later.length > 0 && (
        <>
          <Text as="h2" $weight="Regular" $size={300} $color={Gray700} $uppercase>
            {t('component.practitionerList.availableLater')}
          </Text>
          <Tiles
            appointments={later}
            variant="specialist"
            onAppointmentClick={(appointment) => {
              onPractitionerSelect(
                appointment.specialistId,
                selectedDate,
                appointment.appointmentId
              )
            }}
            onSpecialistClick={(appointment) => {
              onSpecialistClick(appointment)
            }}
            remoteSupported={remoteSupported}
            notifications={[]}
            onDcNotificationClick={() => {}}
            focusIndex={indexRef.current - first.length}
          />
        </>
      )}
      <LoadMoreContainer>
        {(hasNextPage || isLoading) && (
          <LoadingButton
            onClick={() => {
              indexRef.current = first.length + later.length
              fetchNextPage()
            }}
            isLoading={isLoading}
            dataCy="practitionerList-loadMoreButton"
          />
        )}
      </LoadMoreContainer>
    </Container>
  )
}

export default PractitionerList
